import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Divider, Alert, Tooltip, TextField, MenuItem,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Description as NoticeIcon,
  Edit as EditIcon,
  Undo as UndoIcon,
  EventBusy as TerminateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import ErpDateField from '../ErpDateField';
import { formatDate, formatDateTime } from '../formatDate';
import {
  EXPIRATION_NOTICE_CAUSE_CODE,
  SETTLEMENT_DEFAULT_DAYS,
  addDaysIso,
  daysUntil,
  missingReasons,
  downloadTerminationNotices,
} from './terminationNotices';

const Row = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.5 }}>
    <strong>{label}:</strong> {value ?? '—'}
  </Typography>
);

const seconds = (ts) => ts?._seconds ?? ts?.seconds ?? null;

/**
 * Término del contrato (PLAN_ERP.md §15, 2026-10-19): registrar / editar /
 * deshacer con la causal legal (catálogo fijo del backend) y, con la causal
 * 159-4, descargar el aviso de vencimiento de plazo para imprimirlo y
 * entregarlo a la persona. El finiquito de $0 solo existe en la carta EST.
 */
const ErpContractTerminationCard = ({ contract, quotation, terminationNotice, onChanged }) => {
  const { t, i18n } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const [causes, setCauses] = useState([]);
  const [meta, setMeta] = useState({
    expirationNoticeCauseCode: EXPIRATION_NOTICE_CAUSE_CODE,
    settlementDefaultDays: SETTLEMENT_DEFAULT_DAYS,
  });
  const [causesError, setCausesError] = useState(false);
  const [form, setForm] = useState(null); // diálogo abierto si no es null
  const [removeOpen, setRemoveOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const termination = contract.termination || null;
  const startDate = contract.dates?.startDate || null;
  const endDate = contract.dates?.endDate || null;
  const isEst = quotation?.serviceMode === 'temporary_services';
  const expirationCode = meta.expirationNoticeCauseCode;
  const settlementDays = meta.settlementDefaultDays;
  const k = (key) => `erp.contracts.termination.${key}`;

  useEffect(() => {
    const loadCauses = async () => {
      try {
        setCausesError(false);
        const response = await api.get('/erp/termination-causes', {
          params: { organizationId: organization },
          ...headers,
        });
        if (response.data.response?.status_response === 'OK') {
          setCauses(response.data.response.data || []);
          if (response.data.response.meta) setMeta(response.data.response.meta);
        } else {
          setCausesError(true);
        }
      } catch (err) {
        console.error(err);
        setCausesError(true);
      }
    };
    if (organization) loadCauses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const openDialog = () => {
    if (termination) {
      setForm({
        causeCode: termination.causeCode,
        terminationDate: termination.terminationDate || '',
        letterDate: termination.letterDate || termination.terminationDate || '',
        settlementAvailableFrom: termination.settlementAvailableFrom || '',
        settlementZeroPayment: Boolean(termination.settlementZeroPayment),
        notes: termination.notes || '',
        // Si se cambió a mano, ya no sigue a la fecha de término
        letterTouched: Boolean(termination.letterDate) && termination.letterDate !== termination.terminationDate,
        settlementTouched:
          Boolean(termination.settlementAvailableFrom) &&
          termination.settlementAvailableFrom !== addDaysIso(termination.terminationDate, settlementDays),
      });
      return;
    }
    // Contrato con vencimiento: parte en 159-4 con la fecha de vencimiento
    setForm({
      causeCode: endDate ? expirationCode : '',
      terminationDate: endDate || '',
      letterDate: endDate || '',
      settlementAvailableFrom: endDate ? addDaysIso(endDate, settlementDays) : '',
      settlementZeroPayment: false,
      notes: '',
      letterTouched: false,
      settlementTouched: false,
    });
  };

  const setTerminationDate = (value) =>
    setForm((f) => ({
      ...f,
      terminationDate: value,
      letterDate: f.letterTouched ? f.letterDate : value,
      settlementAvailableFrom: f.settlementTouched || !value ? f.settlementAvailableFrom : addDaysIso(value, settlementDays),
    }));

  const setCause = (code) =>
    setForm((f) => {
      const next = { ...f, causeCode: code };
      if (code !== expirationCode) next.settlementZeroPayment = false;
      if (code === expirationCode && endDate && !f.terminationDate) {
        next.terminationDate = endDate;
        if (!f.letterTouched) next.letterDate = endDate;
        if (!f.settlementTouched) next.settlementAvailableFrom = addDaysIso(endDate, settlementDays);
      }
      return next;
    });

  const isExpiration = form?.causeCode === expirationCode;
  const selectedCause = causes.find((c) => c.code === form?.causeCode) || null;

  // Mismas reglas que el backend (422), para avisar antes de guardar
  let formError = null;
  if (form) {
    if (isExpiration && !endDate) formError = t(k('errors.EXPIRATION_REQUIRES_END_DATE'));
    else if (form.terminationDate && startDate && form.terminationDate < startDate) formError = t(k('errors.TERMINATION_BEFORE_START'));
    else if (isExpiration && form.letterDate && startDate && form.letterDate < startDate) formError = t(k('errors.LETTER_BEFORE_START'));
    else if (form.settlementAvailableFrom && form.terminationDate && form.settlementAvailableFrom < form.terminationDate) {
      formError = t(k('errors.SETTLEMENT_BEFORE_TERMINATION'));
    }
  }
  const canSave = Boolean(form?.causeCode && form?.terminationDate) && !formError;

  const errorMessage = (err, fallbackKey) => {
    const code = err.response?.data?.response?.code;
    return code && i18n.exists(k(`errors.${code}`)) ? t(k(`errors.${code}`)) : t(k(fallbackKey));
  };

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setWorking(true);
      const response = await api.put(
        `/erp/contracts/${contract.uid}/termination`,
        {
          organizationId: organization,
          causeCode: form.causeCode,
          terminationDate: form.terminationDate,
          letterDate: isExpiration ? form.letterDate || null : null,
          settlementAvailableFrom: form.settlementAvailableFrom || null,
          settlementZeroPayment: isExpiration && isEst ? form.settlementZeroPayment : false,
          notes: form.notes.trim() || null,
        },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        showAlert(t(k('saveSuccess')), 'success');
        setForm(null);
        if (onChanged) onChanged();
      }
    } catch (err) {
      console.error(err);
      showAlert(errorMessage(err, 'saveError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleRemove = async () => {
    try {
      setWorking(true);
      const response = await api.delete(`/erp/contracts/${contract.uid}/termination`, {
        params: { organizationId: organization },
        ...headers,
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t(k('removeSuccess')), 'success');
        setRemoveOpen(false);
        if (onChanged) onChanged();
      }
    } catch (err) {
      console.error(err);
      showAlert(t(k('removeError')), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleDownload = async () => {
    try {
      setWorking(true);
      await downloadTerminationNotices({
        organization,
        authToken,
        contractIds: [contract.uid],
        fileName: `aviso_vencimiento_${contract.workerRut || contract.uid}.pdf`,
      });
    } catch (err) {
      if (err.code === 'NOTICE_DATA_MISSING') {
        showAlert(t(k('noticeMissing'), { reasons: missingReasons(t, err.issues?.[0]?.missing) }), 'warning');
      } else {
        console.error(err);
        showAlert(t(k('noticeError')), 'error');
      }
    } finally {
      setWorking(false);
    }
  };

  // Qué le falta al aviso (detalle del contrato → terminationNotice)
  const noticeMissing = (terminationNotice?.missing || []).filter(
    (code) => code !== 'termination' && code !== 'expirationCause'
  );
  const noticeBlockedReason = noticeMissing.length > 0
    ? t(k('noticeMissing'), { reasons: missingReasons(t, noticeMissing) })
    : null;
  const hasExpiration = termination?.causeCode === expirationCode;

  let statusText = t(k('activeOpenEnded'));
  if (endDate) {
    const days = daysUntil(endDate);
    if (days < 0) statusText = t(k('expiredWithoutTermination'), { date: formatDate(endDate) });
    else if (days === 0) statusText = t(k('endsToday'), { date: formatDate(endDate) });
    else statusText = t(k('endsOn'), { date: formatDate(endDate), count: days });
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>{t(k('title'))}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {termination ? (
            <>
              {hasExpiration && (
                <Tooltip title={noticeBlockedReason || t(k('downloadNoticeHint'))}>
                  <span>
                    <Button size="small" variant="contained" startIcon={<NoticeIcon />}
                      disabled={working || Boolean(noticeBlockedReason)} onClick={handleDownload}
                      sx={{ textTransform: 'none' }}>
                      {t(k('downloadNotice'))}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button size="small" variant="outlined" startIcon={<EditIcon />} disabled={working || causes.length === 0}
                onClick={openDialog} sx={{ textTransform: 'none' }}>
                {t(k('edit'))}
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<UndoIcon />} disabled={working}
                onClick={() => setRemoveOpen(true)} sx={{ textTransform: 'none' }}>
                {t(k('remove'))}
              </Button>
            </>
          ) : (
            <Button size="small" variant="contained" startIcon={<TerminateIcon />} disabled={working || causes.length === 0}
              onClick={openDialog} sx={{ textTransform: 'none' }}>
              {t(k('register'))}
            </Button>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      {causesError && <Alert severity="error" sx={{ mb: 1 }}>{t(k('loadCausesError'))}</Alert>}

      {termination ? (
        <>
          <Row label={t(k('cause'))}
            value={`${termination.causeCode} — ${termination.causeName}${termination.causeLegalRef ? ` (${termination.causeLegalRef})` : ''}`} />
          <Row label={t(k('terminationDate'))} value={formatDate(termination.terminationDate)} />
          {hasExpiration && (
            <Row label={t(k('letterDate'))} value={formatDate(termination.letterDate || termination.terminationDate)} />
          )}
          <Row label={t(k('settlementAvailableFrom'))} value={formatDate(termination.settlementAvailableFrom)} />
          {hasExpiration && termination.settlementZeroPayment && (
            <Row label={t(k('zeroPaymentShort'))} value={t(k('yes'))} />
          )}
          {termination.notes && <Row label={t(k('notes'))} value={termination.notes} />}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t(k('registeredAt'), { date: formatDateTime(termination.registeredAt) })}
            {seconds(termination.updatedAt) !== null && seconds(termination.updatedAt) !== seconds(termination.registeredAt)
              ? ` · ${t(k('updatedAt'), { date: formatDateTime(termination.updatedAt) })}`
              : ''}
          </Typography>
          {hasExpiration && noticeBlockedReason && (
            <Alert severity="warning" sx={{ mt: 1 }}>{noticeBlockedReason}</Alert>
          )}
        </>
      ) : (
        <Typography variant="body2" color={endDate && daysUntil(endDate) < 0 ? 'error' : 'text.secondary'}>
          {statusText}
        </Typography>
      )}

      {/* Registrar / editar término */}
      <Dialog open={Boolean(form)} onClose={() => !working && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t(k(termination ? 'edit' : 'register'))}</DialogTitle>
        <DialogContent>
          {form && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField select size="small" fullWidth label={t(k('cause'))} value={form.causeCode}
                onChange={(e) => setCause(e.target.value)}
                helperText={selectedCause ? `${selectedCause.legalRef} · ${selectedCause.description}` : ' '}>
                {causes.map((cause) => (
                  <MenuItem key={cause.code} value={cause.code}>{`${cause.code} — ${cause.name}`}</MenuItem>
                ))}
              </TextField>
              <ErpDateField label={t(k('terminationDate'))} value={form.terminationDate}
                onChange={setTerminationDate} minDate={startDate || undefined}
                helperText={isExpiration && endDate && form.terminationDate && form.terminationDate !== endDate
                  ? t(k('dateDiffersFromEnd'), { date: formatDate(endDate) })
                  : undefined} />
              {isExpiration && (
                <ErpDateField label={t(k('letterDate'))} value={form.letterDate}
                  onChange={(value) => setForm((f) => ({ ...f, letterDate: value, letterTouched: true }))}
                  minDate={startDate || undefined} helperText={t(k('letterDateHelper'))} />
              )}
              <ErpDateField label={t(k('settlementAvailableFrom'))} value={form.settlementAvailableFrom}
                onChange={(value) => setForm((f) => ({ ...f, settlementAvailableFrom: value, settlementTouched: true }))}
                minDate={form.terminationDate || undefined}
                helperText={t(k('settlementHelper'), { days: settlementDays })} />
              {isExpiration && isEst && (
                <FormControlLabel
                  control={(
                    <Checkbox checked={form.settlementZeroPayment}
                      onChange={(e) => setForm((f) => ({ ...f, settlementZeroPayment: e.target.checked }))} />
                  )}
                  label={t(k('zeroPayment'))} />
              )}
              <TextField size="small" fullWidth multiline minRows={2} label={t(k('notes'))} value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                inputProps={{ maxLength: 1000 }} />
              {formError && <Alert severity="warning">{formError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={working} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={working || !canSave} sx={{ textTransform: 'none' }}>
            {working ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deshacer término */}
      <Dialog open={removeOpen} onClose={() => !working && setRemoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t(k('remove'))}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t(k('removeConfirm'))}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)} disabled={working} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleRemove} disabled={working} sx={{ textTransform: 'none' }}>
            {t(k('remove'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ErpContractTerminationCard;
